import { Component } from 'react';

import DrawManager from './managers/draw-manager';
import CanvasManager from './managers/canvas-manager';
import GuidelineManager from './managers/guideline-manager';
import TimeoutManager from './managers/timeout-manager';
import AudioManager from './managers/audio-manager';
import { AnimationType, Language, CharacterType } from 'types/enums';
import Animations from './animations/animations';
import Spinner from '../spinner';
import CaptionDisplay from './caption-display';

class DrawStage extends Component<any, any> {
  enabledAnimationTypes: any[];
  _animationDuration: any;
  _canvas: any;
  _canvasContainer: any;
  drawManager: DrawManager;
  timeoutManager: any;
  guidelineManager: GuidelineManager;
  canvasManager: any;
  constructor(props) {
    super(props);

    this.state = {
      activeAnimation: '',
      animationStarted: false,
      animationDone: false,
      captions: [],
      allCaptionsVisible: false,
      showAudioLoadSpinner: false,
    };

    this.enabledAnimationTypes = [
      AnimationType.FROG_JUMP,
      AnimationType.DIVE_DOWN,
      AnimationType.DIVE_DOWN_DEEP,
      AnimationType.HELICOPTER,
      AnimationType.HIGH_HELICOPTER,
      AnimationType.MAGIC_C,
      AnimationType.SLIDE_DOWN,
      AnimationType.SLIDE_DOWN_REVERSED,
      AnimationType.KARATE_KICK,
      AnimationType.SLIDE_AWAY,
      AnimationType.BASEBALL,
      AnimationType.BUMP,
      AnimationType.BUMP_INNER,
      AnimationType.SWIM,
      AnimationType.LONG_SWIM,
      AnimationType.SMILE,
      AnimationType.FIREFIGHTER,
      AnimationType.JET,
      AnimationType.TOW,
    ];
    this._animationDuration = 2;
    this._canvas = null;
    this._canvasContainer = null;
    this.drawManager = new DrawManager({ strokeWidth: 10 });

    this.timeoutManager = new TimeoutManager();
    this.guidelineManager = new GuidelineManager();
    this.canvasManager = new CanvasManager();
  }

  componentDidMount() {
    if (this.props.svgPath?.default) {
      this.loadSVG();
    }
  }

  componentWillUnmount() {
    this.drawManager.stopDrawing();
    AudioManager.clearDownloadTimeout();
    this.timeoutManager.clearAll();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props?.svgPath?.default &&
      prevProps?.svgPath.default !== this.props.svgPath?.default
    ) {
      this.loadSVG();
    }
    if (!prevProps.gameStarted && this.props.gameStarted) {
      this.downloadAudio();
    }
    if (
      this.props.currentPathIndex !== -1 &&
      this.props.currentPathIndex !== prevProps.currentPathIndex &&
      this.props.currentPathIndex !== Infinity &&
      this.guidelineManager.guidelineSVGs[this.props.currentPathIndex]
    ) {
      this.handleNextPath();
    }

    if (
      this.props.currentPathIndex === Infinity &&
      prevProps.currentPathIndex !== Infinity
    ) {
      this.props.onToggleDrawInProgress(true);

      this.drawCharacter({
        onDrawPathStart: this.handleContinuousDrawPathStart,
        onDrawPathEnd: this.handleContinuousDrawPathEnd,
        onCharacterDrawEnd: () => {
          this.props.onToggleDrawInProgress(false);
          this.props.onToggleDrawFinished(true);
        },
      });
    }

    if (
      this.props.currentPathIndex === -1 &&
      prevProps.currentPathIndex !== -1
    ) {
      this.handleClearModel();
    }
  }

  loadSVG = async () => {
    await this.canvasManager.initialize(this._canvas);

    const svg = await this.canvasManager.loadSVG(
      this.props.svgPath.default,
      this._canvas
    );
    await this.handleSVGLoaded(svg);
  };

  // the following two handlers need to handle both sync and async cases
  handleContinuousDrawPathStart = (pathIndex) => {
    this.displayCaption(pathIndex);
    return new Promise((resolve: any) => {
      if (this.guidelineManager.isFrogJumpPath(pathIndex)) {
        this.playAudio(pathIndex);
        this.setState({
          activeAnimation: AnimationType.FROG_JUMP,
          animationStarted: true,
        });
        this.timeoutManager.add(resolve, this._animationDuration + 0.5);
      } else {
        this.guidelineManager.showArrowSVG(pathIndex);
        if (!this.guidelineManager.getDrawEndAnimationType(pathIndex)) {
          this.timeoutManager.add(() => {
            this.applyPathParams(
              this.guidelineManager.getPathParams(pathIndex),
              pathIndex
            );
            resolve();
          }, 0);
        } else {
          resolve();
        }
      }
    });
  };

  handleContinuousDrawPathEnd = (pathIndex) => {
    const animationType = this.guidelineManager.getDrawEndAnimationType(
      pathIndex
    );

    if (this.state.activeAnimation) {
      this.setState({
        activeAnimation: '',
        animationDone: true,
        animationStarted: false,
      });
    }

    if (pathIndex === this.guidelineManager.guidelineSVGs.length - 1) {
      this.displayAllCaptions();
    }

    this.guidelineManager.hideArrowSVG(pathIndex);

    return new Promise((resolve: any) => {
      if (animationType !== null) {
        this.playAnimationAudio(animationType);

        this.setState({
          activeAnimation: animationType,
          animationDone: false,
          animationStarted: true,
        });

        this.timeoutManager.add(() => {
          this.setState({
            activeAnimation: '',
            animationDone: true,
            animationStarted: false,
          });
          resolve();
        }, this._animationDuration);
      } else {
        resolve();
      }
    });
  };

  handleClearModel() {
    this.drawManager.stopDrawing();
    this.drawManager.removeAllDrawnPaths();
    this.drawManager.drawReferencePoint(this.guidelineManager.guidelineSVGs[0]);
    this.guidelineManager.hideAllArrowSVGs();
    this.timeoutManager.clearAll();
    this.setState({
      activeAnimation: '',
      animationStarted: false,
      animationDone: false,
      captions: [],
      allCaptionsVisible: false,
    });
  }

  downloadAudio() {
    this.props.onAudioDownloadRequest();
    this.timeoutManager.add(() => {
      this.setState({
        showAudioLoadSpinner: true,
      });
    }, 5);

    this.loadAudio().then(() => {
      this.timeoutManager.clearAll();
      if (this.state.showAudioLoadSpinner) {
        this.setState({
          showAudioLoadSpinner: false,
        });
      }
      this.props.onAudioDownloadResponse();
    });
  }

  handleSVGLoaded = async (characterSVG) => {
    if (this.props.tall || this.props.wide) {
      this.canvasManager.scaleSVG(
        this._canvasContainer,
        characterSVG,
        this._canvas
      );
    } else {
      this.canvasManager.scaleSVG(this._canvasContainer, characterSVG);
    }
    this.guidelineManager.getGuidelineSVGs(characterSVG);
    this.guidelineManager.getArrowSVGs(characterSVG);
    this.drawManager.drawReferencePoint(this.guidelineManager.guidelineSVGs[0]);

    if (this.props.gameStarted) {
      this.downloadAudio();
    }
  };

  getLanguage() {
    switch (this.props.languageId) {
      case Language.ENGLISH: {
        return 'english';
      }

      case Language.SPANISH: {
        return 'spanish';
      }

      default: {
        return '';
      }
    }
  }

  loadAudio() {
    let pathParams: any = null;
    const animationAudioPromises: any = [];
    const volume = this.props.volume / 100.0;

    return new Promise((resolve: any) => {
      AudioManager.fetchAudioList({
        language: this.getLanguage(),
        character: this.props.character,
        characterCase: this.props.characterCase,
        characterType: this.props.characterType,
        pathAmount: this.guidelineManager.guidelineSVGs.length,
        volume: volume,
        muted: this.props.muted,
      }).then(() => {
        for (let i = 0; i < this.guidelineManager.guidelineSVGs.length; i++) {
          pathParams = this.guidelineManager.getPathParams(i);

          if (pathParams && pathParams.animation) {
            animationAudioPromises.push(
              AudioManager.fetchAnimationAudio(
                pathParams.animation,
                volume,
                this.props.muted
              )
            );
          }
        }

        Promise.all(animationAudioPromises).then(() => {
          resolve();
        });
      });
    });
  }

  playAudio = (pathIndex) => {
    AudioManager.playAudio(pathIndex);
  };

  playAnimationAudio(animationType) {
    AudioManager.animationAudioEls[animationType].play();
  }

  handleNextPath() {
    this.props.onToggleDrawInProgress(true);

    if (this.state.activeAnimation) {
      this.setState({
        activeAnimation: '',
        animationStarted: false,
        animationDone: true,
      });
    }

    if (this.guidelineManager.isFrogJumpPath(this.props.currentPathIndex)) {
      this.setState({
        activeAnimation: AnimationType.FROG_JUMP,
        animationStarted: true,
      });
      this.playAudio(this.props.currentPathIndex);
    } else {
      this.handleRegularPath();
    }
  }

  displayCaption(pathIndex) {
    const { captions } = this.state;
    const currentCaption = this.guidelineManager.getGuidelineCaption(
      pathIndex,
      this.props.languageId === Language.SPANISH
    );

    if (currentCaption) {
      captions.push(currentCaption);
      this.setState({ captions });
    }
  }

  displayAllCaptions() {
    this.setState({ allCaptionsVisible: true });
  }

  handleRegularPath() {
    const pathParams = this.guidelineManager.getPathParams(
      this.props.currentPathIndex
    );
    const drawEndAnimationType = this.guidelineManager.getDrawEndAnimationType(
      this.props.currentPathIndex
    );

    if (!drawEndAnimationType) {
      this.timeoutManager.add(this.applyPathParams.bind(this, pathParams), 0);
    }

    this.playAudio(this.props.currentPathIndex);
    this.displayCaption(this.props.currentPathIndex);

    if (this.guidelineManager.isDotPath(this.props.currentPathIndex)) {
      this.drawManager.drawDot(
        this.guidelineManager.guidelineSVGs[this.props.currentPathIndex]
      );
      this.handleRegularPathDrawDone();
    } else {
      this.guidelineManager.showArrowSVG(this.props.currentPathIndex);
      this.drawManager
        .drawPath(
          this.guidelineManager.guidelineSVGs[this.props.currentPathIndex],
          AudioManager.getAudioDuration(this.props.currentPathIndex)
        )
        .then(() => {
          if (drawEndAnimationType) {
            this.applyPathParams(pathParams);
          }
          this.handleRegularPathDrawDone();
        });
    }
  }

  handleRegularPathDrawDone() {
    this.guidelineManager.hideArrowSVG(this.props.currentPathIndex);
    if (
      this.props.currentPathIndex ===
      this.guidelineManager.guidelineSVGs.length - 1
    ) {
      this.displayAllCaptions();
      this.props.onToggleDrawFinished(true);
    }

    if (this.guidelineManager.isContinuePath(this.props.currentPathIndex)) {
      this.props.onSetNextPathIndex();
    } else {
      this.props.onToggleDrawInProgress(false);
    }
  }

  applyPathParams(params, pathIndex?) {
    if (params && params.animation) {
      if (params.animation !== AnimationType.BASEBALL) {
        this.playAnimationAudio(params.animation);
      }

      this._animationDuration =
        pathIndex !== undefined
          ? AudioManager.getAudioDuration(pathIndex)
          : AudioManager.getAudioDuration(this.props.currentPathIndex);

      this.setState({
        activeAnimation: params.animation,
        animationStarted: true,
      });
    }

    if (params && params.playFormerAnimationSound) {
      this.playAnimationAudio(params.playFormerAnimationSound);
    }
  }

  drawCharacter({
    onDrawPathStart,
    onDrawPathEnd,
    onCharacterDrawEnd = () => {},
    index = 0,
  }) {
    onDrawPathStart(index).then(() => {
      if (!this.guidelineManager.isFrogJumpPath(index)) {
        this.playAudio(index);
      }

      if (this.guidelineManager.isDotPath(index)) {
        this.drawManager.drawDot(this.guidelineManager.guidelineSVGs[index]);
        onDrawPathEnd(index).then(() => {
          if (
            this.drawManager.drawnPaths.length <
            this.guidelineManager.guidelineSVGs.length
          ) {
            this.drawCharacter({
              onDrawPathStart,
              onDrawPathEnd,
              onCharacterDrawEnd,
              index: index + 1,
            });
          } else {
            onCharacterDrawEnd();
          }
        });
      } else {
        this.drawManager
          .drawPath(
            this.guidelineManager.guidelineSVGs[index],
            AudioManager.getAudioDuration(index)
          )
          .then(() => {
            onDrawPathEnd(index).then(() => {
              if (
                this.drawManager.drawnPaths.length <
                this.guidelineManager.guidelineSVGs.length
              ) {
                this.drawCharacter({
                  onDrawPathStart,
                  onDrawPathEnd,
                  onCharacterDrawEnd,
                  index: index + 1,
                });
              } else {
                onCharacterDrawEnd();
              }
            });
          });
      }
    });
  }

  renderLineBackground() {
    return (
      <div
        className={`line-background ${this.props.showLines ? 'visible' : ''} ${
          this.props.lowercase ? 'lowercase' : ''
        }`}
      >
        <div />
        <div />
      </div>
    );
  }

  handleAnimationComplete = () => {
    if (this.guidelineManager.isFrogJumpPath(this.props.currentPathIndex)) {
      this.props.onToggleDrawInProgress(false);
    }
  };

  getCanvasContainerClassName() {
    let className = 'canvas-container';

    className += this.props.showBackground ? ' has-background' : '';
    className += this.props.lowercase ? ' lowercase' : '';
    className += this.props.spanishN ? ' spanish-n' : '';
    className += this.props.tall ? ' tall' : '';
    className += this.props.wide ? ' wide' : '';

    return className;
  }

  getCaptionOffsetClassName() {
    const descenderCharacters = [
      'a',
      'd',
      'g',
      'f',
      'j',
      'l',
      'p',
      'q',
      't',
      'y',
      'z',
      'Y',
      'Z',
      'J',
    ];
    let className = '';

    for (let i = 0; i < descenderCharacters.length; i++) {
      if (descenderCharacters[i] === this.props.character) {
        className = 'has-descender';
        className +=
          (this.props.character === 'p' || this.props.character === 't') &&
          this.props.characterType === CharacterType.PRINT
            ? ' extra-left'
            : '';
      }
    }

    className +=
      this.props.character === 'm' &&
      this.props.characterType === CharacterType.CURSIVE
        ? ' cursive-m'
        : '';

    return className;
  }

  renderAnimations() {
    return (
      <Animations
        animationTypes={this.enabledAnimationTypes}
        activeAnimationType={this.state.activeAnimation}
        canvas={this._canvas}
        animationStarted={this.state.animationStarted}
        animationDone={this.state.animationDone}
        duration={this._animationDuration}
        onAnimationComplete={this.handleAnimationComplete}
      />
    );
  }

  render() {
    return (
      <div
        className={`draw-stage ${this.props.characterType} ${this.props.characterCase}`}
      >
        {this.renderLineBackground()}
        <div
          className={this.getCanvasContainerClassName()}
          ref={(divEl) => (this._canvasContainer = divEl)}
        >
          {this.renderLineBackground()}
          <canvas
            ref={(canvasEl) => {
              this._canvas = canvasEl;
            }}
          />
          <Spinner show={this.state.showAudioLoadSpinner} />
          <CaptionDisplay
            captions={this.state.captions}
            characterType={this.props.characterType}
            characterCase={this.props.characterCase}
            offsetClassName={this.getCaptionOffsetClassName()}
            showAll={this.state.allCaptionsVisible}
            textSize={this.props.textSize}
          />
        </div>
        {this.renderAnimations()}
      </div>
    );
  }
}

export default DrawStage;
